import TableEcho, { TableCellEcho, TableRowEcho, Column } from '@/components/TableEcho';
import { Radio } from 'antd';

/**
 * A reusable table component for platform selection with radio buttons.
 * Displays a list of platforms as table rows, each with a radio button for selection
 * and customizable data cells rendered via a render function.
 *
 * @template TData - The type of data associated with each platform
 * @template TRow - The type of row data associated with each platform
 *
 * @param data - The data to display in the table
 * @param selectedPlatform - The currently selected platform key
 * @param handlePlatformChange - Callback function invoked when a platform is selected via radio button
 * @param tableColumns - Array of column definitions for the table structure
 * @param renderDataCells - Function that renders the data cells for each platform row, receiving the platform's data as input
 * @param idPrefix - A prefix for the id and name of the radio buttons
 * @example
 * ```tsx
 * <PlatformSelectionTableEcho
 *   data={platformData}
 *   selectedPlatform="Platform A"
 *   handlePlatformChange={(platform) => setSelectedPlatform(platform)}
 *   tableColumns={[
 *     { key: 'top_age_group', header: 'Top age group', align: 'left', className: 'p-2' },
 *     { key: 'median_age', header: 'Median age', align: 'right', className: 'p-2' }
 *   ]}
 *   renderDataCells={(rowData) => (
 *     <>
 *       <TableCellEcho align="left" className="px-2">{rowData.topAgeGroup}</TableCellEcho>
 *       <TableCellEcho align="right" className="px-2">{rowData.medianAge}</TableCellEcho>
 *     </>
 *   )}
 *   idPrefix="age-data"
 * />
 * ```
 *
 * @returns A table component with selectable platform rows
 *
 */

export const PlatformSelectionTableEcho = <
  TData extends { plays: TRow[] },
  TRow extends { platform: string },
>({
  data,
  selectedPlatform,
  handlePlatformChange,
  tableColumns,
  renderDataCells,
  idPrefix,
}: {
  data: TData;
  selectedPlatform: TRow['platform'];
  handlePlatformChange: (platform: TRow['platform']) => void;
  tableColumns: Column[];
  renderDataCells: (rowData: TRow) => React.ReactNode;
  idPrefix: string;
}) => {
  return (
    <TableEcho columns={tableColumns} className="border-collapse max-w-full">
      {data.plays?.length > 0 &&
        data.plays.map((rowData) => (
          <TableRowEcho
            key={`${idPrefix}-${rowData.platform}`}
            className={`${selectedPlatform === rowData.platform ? 'bg-[#F8F8FC]' : ''}`}
          >
            <TableCellEcho align="left" className="px-2">
              <Radio
                value={rowData.platform}
                name={`${idPrefix}-${rowData.platform}`}
                id={`${idPrefix}-${rowData.platform}`}
                onChange={() => handlePlatformChange(rowData.platform)}
                checked={selectedPlatform === rowData.platform}
              />
            </TableCellEcho>
            <TableCellEcho className="text-textDarkGrey font-medium px-2">
              {rowData.platform}
            </TableCellEcho>
            {renderDataCells(rowData)}
          </TableRowEcho>
        ))}
    </TableEcho>
  );
};
